/**
 * Review Settings Manager
 * 
 * Handles persistent storage of review page settings:
 * - User Settings: Cross-session preferences (localStorage)
 * - Job Settings: Per-job session settings (sessionStorage)
 * 
 * User Settings (persistent across sessions):
 * - Items per page preference
 * - Default language visibility preferences
 * - Search preferences
 * 
 * Job Settings (session-specific):
 * - Column visibility for specific translation jobs
 * - Current page position
 * - Active search terms
 */

class ReviewSettingsManager {
  constructor() {
    this.USER_SETTINGS_KEY = 't8r_review_user_settings'
    this.JOB_SETTINGS_KEY = 't8r_review_job_settings'
    
    // Default user settings
    this.defaultUserSettings = {
      itemsPerPage: 25,
      preferredLanguageDisplay: 'all', // 'all', 'source_and_targets', 'minimal'
      searchDebounceMs: 300,
      showPerformanceInfo: false
    }
    
    // Default job settings template
    this.defaultJobSettings = {
      visibleLanguages: null, // null means use auto-detection
      currentPage: 1,
      searchValue: '',
      lastVisited: null
    }
  }

  /**
   * USER SETTINGS (Cross-session persistence)
   */
  
  getUserSettings() {
    try {
      const stored = localStorage.getItem(this.USER_SETTINGS_KEY)
      const parsed = stored ? JSON.parse(stored) : {}
      
      // Merge with defaults to handle new settings
      return {
        ...this.defaultUserSettings,
        ...parsed
      }
    } catch (error) {
      console.warn('Failed to load user settings:', error)
      return this.defaultUserSettings
    }
  }

  saveUserSettings(settings) {
    try {
      const currentSettings = this.getUserSettings()
      const updatedSettings = {
        ...currentSettings,
        ...settings,
        lastUpdated: new Date().toISOString()
      }
      
      localStorage.setItem(this.USER_SETTINGS_KEY, JSON.stringify(updatedSettings))
      console.log('✅ User settings saved:', updatedSettings)
      return updatedSettings
    } catch (error) {
      console.error('Failed to save user settings:', error)
      return settings
    }
  }

  updateUserSetting(key, value) {
    const settings = this.getUserSettings()
    settings[key] = value
    return this.saveUserSettings(settings)
  }

  // Specific user setting methods
  setItemsPerPage(itemsPerPage) {
    return this.updateUserSetting('itemsPerPage', itemsPerPage)
  }

  getItemsPerPage() {
    return this.getUserSettings().itemsPerPage
  }

  setLanguageDisplayPreference(preference) {
    return this.updateUserSetting('preferredLanguageDisplay', preference)
  }

  getLanguageDisplayPreference() {
    return this.getUserSettings().preferredLanguageDisplay
  }

  /**
   * JOB SETTINGS (Session persistence)
   */
  
  getJobSettings(jobId) {
    try {
      const stored = sessionStorage.getItem(this.JOB_SETTINGS_KEY)
      const allJobSettings = stored ? JSON.parse(stored) : {}
      
      const jobSettings = allJobSettings[jobId] || {}
      
      return {
        ...this.defaultJobSettings,
        ...jobSettings
      }
    } catch (error) {
      console.warn(`Failed to load job settings for ${jobId}:`, error)
      return this.defaultJobSettings
    }
  }

  saveJobSettings(jobId, settings) {
    try {
      const stored = sessionStorage.getItem(this.JOB_SETTINGS_KEY)
      const allJobSettings = stored ? JSON.parse(stored) : {}
      
      const currentJobSettings = allJobSettings[jobId] || {}
      const updatedJobSettings = {
        ...currentJobSettings,
        ...settings,
        lastUpdated: new Date().toISOString()
      }
      
      allJobSettings[jobId] = updatedJobSettings
      
      sessionStorage.setItem(this.JOB_SETTINGS_KEY, JSON.stringify(allJobSettings))
      console.log(`✅ Job settings saved for ${jobId}:`, updatedJobSettings)
      return updatedJobSettings
    } catch (error) {
      console.error(`Failed to save job settings for ${jobId}:`, error)
      return settings
    }
  }

  updateJobSetting(jobId, key, value) {
    const settings = this.getJobSettings(jobId)
    settings[key] = value
    return this.saveJobSettings(jobId, settings)
  }

  // Specific job setting methods
  setVisibleLanguages(jobId, languages) {
    return this.updateJobSetting(jobId, 'visibleLanguages', languages)
  }

  getVisibleLanguages(jobId) {
    return this.getJobSettings(jobId).visibleLanguages
  }

  setCurrentPage(jobId, page) {
    return this.updateJobSetting(jobId, 'currentPage', page)
  }

  getCurrentPage(jobId) {
    return this.getJobSettings(jobId).currentPage
  }

  setSearchValue(jobId, searchValue) {
    return this.updateJobSetting(jobId, 'searchValue', searchValue)
  }

  getSearchValue(jobId) {
    return this.getJobSettings(jobId).searchValue
  }

  markJobVisited(jobId) {
    return this.updateJobSetting(jobId, 'lastVisited', new Date().toISOString())
  }

  /**
   * INITIALIZATION HELPERS
   */

  initializeJobSettings(jobId, languageInfo) {
    const existingSettings = this.getJobSettings(jobId)
    const userSettings = this.getUserSettings()
    
    // Determine initial visible languages if not set
    if (existingSettings.visibleLanguages === null && languageInfo) {
      let initialLanguages = []
      
      switch (userSettings.preferredLanguageDisplay) {
        case 'minimal':
          // Show only source language
          initialLanguages = languageInfo.sourceLanguage ? [languageInfo.sourceLanguage] : languageInfo.allLanguages.slice(0, 1)
          break
        case 'source_and_targets':
          // Show source + target languages (no extra columns)
          initialLanguages = languageInfo.sourceLanguage ? 
            [languageInfo.sourceLanguage, ...languageInfo.targetLanguages] :
            languageInfo.allLanguages
          break
        case 'all':
        default:
          // Show all available languages
          initialLanguages = [...languageInfo.allLanguages]
          break
      }
      
      this.setVisibleLanguages(jobId, initialLanguages)
      existingSettings.visibleLanguages = initialLanguages
    }
    
    // Mark job as visited
    this.markJobVisited(jobId)
    
    return existingSettings
  }

  /**
   * CLEANUP AND MAINTENANCE
   */

  // Clean up old job settings (keep last 50 jobs)
  cleanupJobSettings() {
    try {
      const stored = sessionStorage.getItem(this.JOB_SETTINGS_KEY)
      if (!stored) return
      
      const allJobSettings = JSON.parse(stored)
      const entries = Object.entries(allJobSettings)
      
      if (entries.length <= 50) return
      
      // Sort by lastUpdated and keep most recent 50
      const sortedEntries = entries.sort((a, b) => {
        const timeA = new Date(a[1].lastUpdated || 0).getTime()
        const timeB = new Date(b[1].lastUpdated || 0).getTime()
        return timeB - timeA
      })
      
      const cleanedSettings = Object.fromEntries(sortedEntries.slice(0, 50))
      sessionStorage.setItem(this.JOB_SETTINGS_KEY, JSON.stringify(cleanedSettings))
      
      console.log(`🧹 Cleaned up job settings, kept ${Object.keys(cleanedSettings).length} entries`)
    } catch (error) {
      console.warn('Failed to cleanup job settings:', error)
    }
  }

  // Clear all job settings (useful for testing)
  clearJobSettings() {
    try {
      sessionStorage.removeItem(this.JOB_SETTINGS_KEY)
      console.log('🗑️ Cleared all job settings')
    } catch (error) {
      console.warn('Failed to clear job settings:', error)
    }
  }

  // Reset user settings to defaults
  resetUserSettings() {
    try {
      localStorage.removeItem(this.USER_SETTINGS_KEY)
      console.log('🔄 Reset user settings to defaults')
      return this.defaultUserSettings
    } catch (error) {
      console.warn('Failed to reset user settings:', error)
      return this.defaultUserSettings
    }
  }

  /**
   * EXPORT/IMPORT SETTINGS
   */

  exportSettings() {
    return {
      userSettings: this.getUserSettings(),
      timestamp: new Date().toISOString(),
      version: '1.0'
    }
  }

  importSettings(settingsData) {
    try {
      if (settingsData.userSettings) {
        this.saveUserSettings(settingsData.userSettings)
      }
      console.log('✅ Settings imported successfully')
      return true
    } catch (error) {
      console.error('Failed to import settings:', error)
      return false
    }
  }

  /**
   * DEBUG AND MONITORING
   */

  getDebugInfo() {
    const stored = sessionStorage.getItem(this.JOB_SETTINGS_KEY)
    const allJobSettings = stored ? JSON.parse(stored) : {}
    
    return {
      userSettings: this.getUserSettings(),
      jobSettingsCount: Object.keys(allJobSettings).length,
      recentJobs: Object.entries(allJobSettings)
        .sort((a, b) => new Date(b[1].lastVisited || 0) - new Date(a[1].lastVisited || 0))
        .slice(0, 5)
        .map(([jobId, settings]) => ({
          jobId,
          lastVisited: settings.lastVisited,
          visibleLanguages: settings.visibleLanguages?.length || 0
        })),
      storageUsage: {
        localStorage: JSON.stringify(this.getUserSettings()).length,
        sessionStorage: JSON.stringify(allJobSettings).length
      }
    }
  }
}

// Create singleton instance
const reviewSettingsManager = new ReviewSettingsManager()

export default reviewSettingsManager